/* eslint-disable react/display-name */
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useHistory, useRouteMatch, useParams } from 'react-router-dom';
import { DataGrid, PopupMenu, DialogBox, Button } from 'quantux-ui';
import { addAppAlert } from '../../actions';
import noPinIcon from '../../imgs/no-pin.svg';
import leftPinIcon from '../../imgs/left-pin.svg';
import rightPinIcon from '../../imgs/right-pin.svg';
import { BASE_URL, debugLogToConsole, getPathParts } from '../../utils';
import { salmon, gray } from '../../theme';

const DIALOG_WIDTH = window.innerWidth <= 576 ? (window.innerWidth - 16) : 350;

const defaultCellStyle = {
  font: '12px Lato',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  lineHeight: '14px'
};

const icons = {
  filter: { isSvg: false, path: '', class: 'fa fa-filter' },
  stringSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  stringSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  numberSortAscending: { isSvg: false, class: 'fa fa-numeric-asc' },
  numberSortDescending: { isSvg: false, class: 'fa fa-numeric-desc' },
  dateSortAscending: { isSvg: false, class: 'fa fa-alpha-asc' },
  dateSortDescending: { isSvg: false, class: 'fa fa-alpha-desc' },
  leftPin: { isSvg: true, path: leftPinIcon },
  rightPin: { isSvg: true, path: rightPinIcon },
  noPin: { isSvg: true, path: noPinIcon },
  menuIcon: { isSvg: false, class: 'fa fa-ellipsis-v' }
};

const DOCS_PER_PAGE = 10;

const headerDefs = [
  // Practice Name
  {
    visible: true,
    dataType: 'string',
    pin: 'left',
    sortable: false,
    sortOrder: 0,
    enableCellClick: true,
    text: 'Practice Name',
    field: 'practice',
    key: 'practice',
    isCustomFilter: false,
    showFilterHint: false,
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 281, minWidth: 281, maxWidth: 281, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      const data = text.split('/');
      return (
        <React.Fragment>
          <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
            {data[0]}
          </div>
          <div style={{ color: '#0080FF', cursor: 'pointer', fontSize: '12px' }}>
            {`NPI: ${data[1]}`}
          </div>
        </React.Fragment>
      );
    }
  },
  // Mailing Address
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Primary Practice Address',
    field: 'mailingAddress',
    key: 'mailingAddress',
    showFilterHint: true,
    isCustomFilter: true,
    filterHint: 'Enter city, state code, or ZIP',
    filter: true,
    filterType: 'exactMatch',
    filterMenuType: 'basic', // possible  options => basic, advanced, none
    basicFilterType: 'exactMatch', // possible values are => startsWith, contains, exactMatch, endsWith
    advancedFilterType: 'containsAny', // possible values are => select, containsAll, conainsAny
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    colStyles: { width: 240, minWidth: 240, maxWidth: 240, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => {
      // Format address and zip
      const addTmp = text.split(' ');
      let zip = addTmp.pop();
      zip = zip.length <= 5 ? zip : zip.substr(0, 5) + '-' + zip.substr(5, 9);
      // Generate Google map url
      // const addressUrl = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(text);
      const addressUrl = 'https://www.google.com/maps/search/?api=1&query=' + encodeURI(`${addTmp.join(' ')} ${zip.substr(0, 5)}`);
      return (
        <div className="fx fx__v-center">
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
            style={{ fontSize: '16px' }}
          >
            <i className="fa fa-map-marker" />
          </a>
          <a
            target="_blank"
            href={addressUrl}
            rel="noreferrer"
            className="mr-2"
          >
            <div>
              {addTmp.join(' ') + ' ' + zip}
            </div>
          </a>
        </div>
      );
    }
  },
  // Phone
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Phone',
    field: 'phone',
    key: 'phone',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 123, minWidth: 123, maxWidth: 123, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: text => (
      <div>
        <a
          href={`tel:+1${text}`}
        >
          {`${text.substr(0, 3)}-${text.substr(3, 3)}-${text.substr(6, 4)}`}
        </a>
      </div>
    )
  },
  // Owners
  {
    visible: true,
    dataType: 'string',
    pin: 'none',
    sortable: false,
    sortOrder: 0,
    enableCellClick: false,
    text: 'Admin(s)',
    field: 'owners',
    key: 'owners',
    isCustomFilter: false,
    showFilterHint: false,
    filter: false,
    filterType: 'none',
    disabledFilterTypes: ['startsWith', 'exactMatch', 'contains', 'endsWith', 'advanced'],
    filterMenuType: 'advanced',
    basicFilterType: 'contains',
    advancedFilterType: 'containsAny',
    colStyles: { width: 160, minWidth: 160, maxWidth: 160, textAlign: 'left' },
    headerCellStyle: {},
    colCellsStyles: { textAlign: 'left' },
    footerCellStyle: {},
    cellRenderer: (text) => {
      // console.log(text);
      const names = text.split('/');
      // console.log(names.length);
      // const comps = [];
      // names.forEach((el, idx) => {
      //   comps.push(
      //     <div key={`${idx}_${Date.now()}`}>
      //       {el}
      //     </div>
      //   )
      // });
      // return (
      //   <React.Fragment>
      //     {comps}
      //   </React.Fragment>
      // );
      if (names.length < 3) {
        const comps = [];
        names.forEach((el, idx) => {
          comps.push(
            <div key={`${idx}_${Date.now()}`}>
              {el}
            </div>
          );
        });
        return (
          <React.Fragment>
            {comps}
          </React.Fragment>
        );
      } else {
        const comps = [];
        for (let i = 0; i < 2; i++) {
          comps.push(
            <div key={`${i}_${Date.now()}`}>
              {names[i]}
            </div>
          );
        }
        comps.push(
          <div key={`${names.length + 1}_${Date.now()}`}>
            {`${names.length - 2} more`}
          </div>
        );
        return (
          <React.Fragment>
            {comps}
          </React.Fragment>
        );
      }
    }
  }
];

const ProviderPracticeGrid = (props) => {
  const [showLoader, setShowLoader] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({});
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showGridEllipsisMenu, setShowGridEllipsisMenu] = useState(false);
  const [cursorX, setCursorX] = useState(0);
  const [cursorY, setCursorY] = useState(0);
  const [popupMenuIndex, setPopupMenuIndex] = useState(-1);
  const [showDeleteWarningDialog, setShowDeleteWarningDialog] = useState(false);

  const history = useHistory();
  const { path, url } = useRouteMatch();
  debugLogToConsole(useRouteMatch());

  // Refs
  const gridEllipsisMenu = useRef();

  // Params
  const { providerId } = useParams();

  // Effects
  useEffect(() => {
    getData();
  }, []);

  // --- Styles
  const styles = StyleSheet.create({
    gridEllipsisMenuParent: {
      position: 'fixed',
      height: '64px',
      width: '140px',
      background: 'black',
      zIndex: '100'
    },
    gridEllipsisMenuContainer: {
      minWidth: '140px'
    },
    gridEllipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '138px',
      paddingLeft: '8px',
      background: '#103A60',
      ':hover': {
        background: '#0066CC'
      }
    }
  });

  const getData = async () => {
    setShowLoader(true);
    const url = new URL(`${BASE_URL}/practice/getProviderPractices`);
    let pid = '';
    if (props.providerId === undefined) {
      const usr = JSON.parse(localStorage.getItem('usr'));
      pid = usr._id;
    } else {
      pid = props.providerId;
    }
    const params = { providerId: pid };
    url.search = new URLSearchParams(params).toString();

    fetch(url)
      .then(response => response.text())
      .then(result => {
        const data = [];
        const reqData = JSON.parse(result);
        const { success, practices } = reqData;
        if (success) {
          for (let i = 0; i < practices.length; i++) {
            const activeRecord = practices[i];
            const add1 = activeRecord.mailingAddress.streetAddress1;
            const suite = ''; // activeRecord.mailingAddress.suite;
            const city = activeRecord.mailingAddress.city;
            const state = activeRecord.mailingAddress.state;
            const zip = activeRecord.mailingAddress.zip;
            let owners = '';
            activeRecord.users.forEach((el, idx) => {
              setShowLoader(false);
              if (el.accessType !== 'Provider') {
                owners += `${el.fullName}/`;
              }
            });

            data.push({
              id: activeRecord._id,
              practice: `${activeRecord.practiceName}/${activeRecord.npi}/${activeRecord._id}`,
              mailingAddress: `${add1} ${suite} ${city}, ${state} ${zip}`,
              phone: activeRecord.phone.replace(/-/g, ''),
              npi: `${activeRecord.npi}`,
              owners: owners.substring(0, owners.length - 1)
            });
          }
          setGridData(data);
        } else {
          props.addAppAlert({
            headerText: 'Error',
            headerIcon: 'fa fa-exclamation-triangle',
            bodyText: 'Failed to get provider practices data! Try again. For further assistance contact MedPros Tech Support.',
            showAlert: true,
            autoDismiss: true,
            autoDismissTime: 5000
          });
        }
        setShowLoader(false);
      })
      .catch(error => console.log('error', error));
  };

  const handleOnClickCell = (col, value) => {
    const idx = gridData.findIndex(el => el.practice === value);
    const practiceNpi = gridData[idx].npi;
    // Bookmark
    if (props.createBookmark) {
      localStorage.setItem('lastBookmark', '/user/practices');
    }
    const currentSection = getPathParts(path)[0] || '';
    if (currentSection !== 'practice') {
      const appLocs = JSON.parse(localStorage.getItem('appLocs')) || [];
      appLocs.push(url);
      localStorage.setItem('appLocs', JSON.stringify(appLocs));
    }
    history.push(`/practice/${practiceNpi}/profile`);
  };

  const handleOnPopupMenu = (e, idx) => {
    setShowGridEllipsisMenu(true);
    setCursorX(e.pageX);
    setCursorY(e.pageY);
    setPopupMenuIndex(idx);
  };

  const handleOnClickShowDetailsMenuItem = () => {
    const practiceNpi = gridData[popupMenuIndex].npi;
    // Bookmark
    if (props.createBookmark) {
      localStorage.setItem('lastBookmark', '/user/practices');
    }
    const currentSection = getPathParts(path)[0] || '';
    if (currentSection !== 'practice') {
      const appLocs = JSON.parse(localStorage.getItem('appLocs')) || [];
      appLocs.push(url);
      localStorage.setItem('appLocs', JSON.stringify(appLocs));
    }
    history.push(`/practice/${practiceNpi}/profile`);
  };

  const handleOnClickDeleteMenuItem = () => {
    setShowDeleteWarningDialog(true);
  };

  const handleOnDeletePracticeAffiliation = () => {
    setShowDeleteWarningDialog(false);
    // console.log(gridData[popupMenuIndex]);
    const practiceNpi = (gridData[popupMenuIndex]).npi;
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    // -- Form Data
    const formData = new FormData();
    formData.append('practiceNpi', practiceNpi);
    const pid = gridData[popupMenuIndex].id;
    formData.append('providerId', providerId);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/practice/deleteProviderFromPractice`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          getData();
        }
      })
      .catch(error => console.log('error', error));
  };

  return (
    <div style={{ width: '804px' }}>
      {/* Delete warning dialog */}
      {showDeleteWarningDialog &&
        <DialogBox
          initWidth={DIALOG_WIDTH}
          top={window.innerWidth <= 576 ? 48 : null}
          left={window.innerWidth <= 576 ? 8 : null}
          initHeight={378}
          isOpen={showDeleteWarningDialog}
          onClose={() => { setShowDeleteWarningDialog(false); }}
          maskBackground="#004080"
          maskOpacity={0.5}
          showHeader
          header={{
            text: 'Delete Warning',
            backColor: salmon[500],
            foreColor: salmon[500],
            overrideTheme: true,
            closeColor: '#FF6060',
            fontStyle: 'bold',
            fontSize: '20px',
            icon: 'fa fa-warning'
          }}
          closeOnMaskClick={false}
        >
          <div className='p-2'>
            {/* Warning message */}
            <div className='pt-2 pb-2'>
              Are you sure about deleting practice affiliation?
            </div>
            <div
              className='fx mt-4 pt-2'
              style={{
                justifyContent: 'flex-end',
                borderTop: `1px solid ${gray[500]}`
              }}
            >
              <Button
                btnType='primary'
                isOutlineBtn
                className='mr-2'
                onClick={() => setShowDeleteWarningDialog(false)}
              >
                Cancel
              </Button>
              <Button
                btnType='warning'
                onClick={handleOnDeletePracticeAffiliation}
              >
                Continue
              </Button>
            </div>
          </div>
        </DialogBox>
      }
      {/* Grid ellipsis menu */}
      <div
        ref={gridEllipsisMenu}
        className={css(styles.gridEllipsisMenuParent)}
        style={{
          top: cursorY,
          left: cursorX - 140,
          display: showGridEllipsisMenu ? 'block' : 'none'
        }}
      >
        <PopupMenu
          top={0}
          left={0}
          position="relative"
          visible={showGridEllipsisMenu}
          parent={gridEllipsisMenu}
          onOutsideClick={() => { setShowGridEllipsisMenu(false); }}
        >
          <div className={css(styles.gridEllipsisMenuContainer)}>
            {/* Show details menu item */}
            <div
              className={css(styles.gridEllipsisMenuItem)}
              onClick={handleOnClickShowDetailsMenuItem}
            >
              Show Details
            </div>
            {/* Delete record menu item */}
            <div
              className={css(styles.gridEllipsisMenuItem)}
              onClick={handleOnClickDeleteMenuItem}
            >
              Delete Record
            </div>
          </div>
        </PopupMenu>
      </div>
      <DataGrid
        loader={showLoader}
        icons={icons}
        headerDefs={headerDefs}
        rowData={gridData}
        filters={filters}
        gridWidth={804}
        gridHeight={300}
        gridHeaderHeight={40}
        gridFooterHeight={0}
        gridContainerHeight="804px"
        rowHeight={60}
        owsPerPage={DOCS_PER_PAGE}
        defaultCellStyle={defaultCellStyle}
        showFooter={false}
        showEllipsisCol
        enableHover
        totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
        currentPage={currentPage}
        onApplyFilter={() => console.log('not implemented yet!')}
        onResetCol={() => console.log('not implemented yet!')}
        onLoadPage={() => console.log('not implemented yet!')}
        onClickCell={handleOnClickCell}
        onColFilterToggle={() => console.log('not implemented yet!')}
        onFilterTypeUpdate={() => console.log('not implemented yet!')}
        onColPinChange={() => console.log('not implemented yet!')}
        onResize={() => console.log('on resize')}
        onPopupMenu={handleOnPopupMenu}
        onChangeTextAlignment={() => console.log('Text alignment changed!')}
        onFilterChange={() => console.log('on filter changed')}
        onColVisibilityChange={() => console.log('on col visibility changed')}
        onResetGrid={() => console.log('on reset grid')}
        onSort={() => { console.log('Column sort order changed!'); }}
        onColConfig={() => console.log('on col config changed')}
      />
    </div>
  );
};

ProviderPracticeGrid.defaultProps = {
  createBookmark: false
};

function mapStateToProps({ user }) {
  return { ...user };
}
export default connect(mapStateToProps, {
  addAppAlert
})(ProviderPracticeGrid);
